/**
 * The Arche Store's server data, from the public store API
 * (`/v1/marketplace/store/*`). Readable signed out; a minute's staleness
 * matches the API's own caching.
 */
import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { StoreAppsPageData } from '@fias/public-site-ui';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import {
  STORE_SNAPSHOT_DATA_UPDATED_AT,
  takeStoreAppSnapshot,
  takeStoreCategoriesSnapshot,
  takeStoreCategorySnapshot,
  takeStoreHomeSnapshot,
  takeStoreStorySnapshot,
  takeStoreTopSnapshot,
} from '@/lib/store-snapshot-data';

const STALE_MS = 60_000;

/** A search runs from this many characters. */
export const STORE_SEARCH_MIN_LENGTH = 2;

/** The page size a category page loads in (the API's most). */
export const STORE_APPS_PAGE_SIZE = 60;

/** Not-found answers are final; anything else is worth one more try. */
function retryUnlessNotFound(failures: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status === 404) return false;
  return failures < 1;
}

export function useStoreHome() {
  return useQuery({
    queryKey: queryKeys.store.home(),
    queryFn: () => api.getStoreHome(),
    staleTime: STALE_MS,
    // A cold load paints the snapshot's data at once, then refreshes it.
    initialData: takeStoreHomeSnapshot,
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

export function useStoreCategories() {
  return useQuery({
    queryKey: queryKeys.store.categories(),
    queryFn: () => api.getStoreCategories(),
    staleTime: STALE_MS,
    initialData: takeStoreCategoriesSnapshot,
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/** Where the next page starts, or undefined when every app is loaded. */
function nextAppsOffset(last: StoreAppsPageData, pages: StoreAppsPageData[]): number | undefined {
  const loaded = pages.reduce((n, page) => n + page.cards.length, 0);
  return last.cards.length > 0 && loaded < last.total ? loaded : undefined;
}

/** One category's apps (or all of them), a page at a time. */
export function useStoreApps(category: string | null, sort: 'popular' | 'newest' | 'name') {
  return useInfiniteQuery({
    queryKey: queryKeys.store.apps(category, sort),
    queryFn: ({ pageParam }) =>
      api.getStoreApps({
        category: category ?? undefined,
        sort,
        offset: pageParam,
        limit: STORE_APPS_PAGE_SIZE,
      }),
    initialPageParam: 0,
    getNextPageParam: nextAppsOffset,
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    placeholderData: keepPreviousData,
    // A snapshot lists a category's apps most popular first.
    initialData:
      category && sort === 'popular' ? () => takeStoreCategorySnapshot(category) : undefined,
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

/** The loaded pages as one: every app once, the newest total. */
export function flattenAppsPages(pages: StoreAppsPageData[]): StoreAppsPageData | null {
  if (pages.length === 0) return null;
  const seen = new Set<string>();
  const cards = pages
    .flatMap((page) => page.cards)
    .filter((card) => {
      if (seen.has(card.ref)) return false;
      seen.add(card.ref);
      return true;
    });
  const last = pages[pages.length - 1];
  return { category: last.category, cards, total: last.total };
}

export function useStoreTop(category: string | null) {
  return useQuery({
    queryKey: queryKeys.store.top(category),
    queryFn: () => api.getStoreTop(category ?? undefined),
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
    // The snapshot is the all-apps chart.
    initialData: category === null ? takeStoreTopSnapshot : undefined,
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

export function useStoreApp(slug: string) {
  return useQuery({
    queryKey: queryKeys.store.app(slug),
    queryFn: () => api.getStoreApp(slug),
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    initialData: () => takeStoreAppSnapshot(slug),
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

export function useStoreStory(slug: string) {
  return useQuery({
    queryKey: queryKeys.store.story(slug),
    queryFn: () => api.getStoreStory(slug),
    staleTime: STALE_MS,
    retry: retryUnlessNotFound,
    initialData: () => takeStoreStorySnapshot(slug),
    initialDataUpdatedAt: STORE_SNAPSHOT_DATA_UPDATED_AT,
  });
}

export function useStoreSuggestions() {
  return useQuery({
    queryKey: queryKeys.store.suggestions(),
    queryFn: () => api.getStoreSuggestions(),
    staleTime: 5 * STALE_MS,
  });
}

/**
 * Store search for `query` (already debounced by the caller). Off below
 * {@link STORE_SEARCH_MIN_LENGTH} characters. Results stay up while the next
 * query's arrive.
 */
export function useStoreSearch(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.store.search(q),
    queryFn: ({ signal }) => api.searchStore(q, { signal }),
    enabled: q.length >= STORE_SEARCH_MIN_LENGTH,
    staleTime: STALE_MS,
    placeholderData: keepPreviousData,
    retry: false,
  });
}
